import React, { useState } from "react";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import { Mail, ShieldCheck, LifeBuoy, MessageSquare, Send, Loader2 } from "lucide-react";

const CONTACTS = [
  { icon: LifeBuoy, label: "Support", email: "support@kalamprime.com", note: "Accounts, playback, subscriptions & billing" },
  { icon: ShieldCheck, label: "Privacy", email: "privacy@kalamprime.com", note: "Data requests, privacy & Kids safety" },
  { icon: MessageSquare, label: "Business", email: "hello@kalamprime.com", note: "Partnerships & press enquiries" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "Support", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const inputCls = "w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-[var(--nik-blue)] transition-colors";

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    // Opens the visitor's mail client pre-filled to the right inbox.
    const to = form.topic === "Privacy" ? "privacy@kalamprime.com"
      : form.topic === "Business" ? "hello@kalamprime.com" : "support@kalamprime.com";
    const subject = encodeURIComponent(`[${form.topic}] Message from ${form.name || "a KalamPrime viewer"}`);
    const body = encodeURIComponent(`${form.message}\n\n— ${form.name}\n${form.email}`);
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    setTimeout(() => { setBusy(false); toast.success("Opening your email app…"); }, 400);
  };

  return (
    <Layout>
      <div className="pt-28 px-4 md:px-12 pb-16 max-w-4xl mx-auto" data-testid="contact-page">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white fade-up">Contact & Support</h1>
        <p className="text-slate-400 mt-2">We're here to help. Reach the right team or send us a message.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {CONTACTS.map((c) => (
            <a key={c.email} href={`mailto:${c.email}`} data-testid={`contact-${c.label.toLowerCase()}`}
              className="bg-[#121824] border border-white/10 rounded-2xl p-5 hover:border-[var(--nik-blue)] transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[var(--nik-blue)] to-[#0047B3] flex items-center justify-center">
                <c.icon size={20} className="text-white" />
              </div>
              <div className="font-display font-bold text-white mt-3">{c.label}</div>
              <div className="text-xs text-slate-400 mt-1">{c.note}</div>
              <div className="flex items-center gap-1.5 text-sm text-[var(--nik-glow)] mt-3 group-hover:underline">
                <Mail size={14} /> {c.email}
              </div>
            </a>
          ))}
        </div>

        <div className="mt-10 bg-[#121824] border border-white/10 rounded-2xl p-6 md:p-8">
          <h2 className="font-display text-2xl font-bold text-white">Send us a message</h2>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="contact-name" required placeholder="Your name" className={inputCls}
                value={form.name} onChange={(e) => set("name", e.target.value)} />
              <input data-testid="contact-email" type="email" required placeholder="Your email" className={inputCls}
                value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <select data-testid="contact-topic" className={inputCls} value={form.topic} onChange={(e) => set("topic", e.target.value)}>
              <option>Support</option>
              <option>Privacy</option>
              <option>Business</option>
            </select>
            <textarea data-testid="contact-message" required placeholder="How can we help?" className={inputCls + " h-32"}
              value={form.message} onChange={(e) => set("message", e.target.value)} />
            <button data-testid="contact-submit" type="submit" disabled={busy}
              className="flex items-center gap-2 bg-[var(--nik-blue)] hover:bg-[var(--nik-blue-hover)] text-white font-semibold px-6 py-3 rounded-lg transition-colors nik-glow-btn disabled:opacity-60">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />} Send Message
            </button>
          </form>
          <p className="text-xs text-slate-500 mt-4">Typical response time: within 24 hours. KalamPrime is a brand by Ishukart.</p>
        </div>
      </div>
    </Layout>
  );
}
